import type { CheckItem } from '@/content/checks'
import { daimiChecks } from '@/content/daimi'
import { daimiAirlinesChecks } from '@/content/daimiAirlines'
import { daimiAirportsChecks } from '@/content/daimiAirports'
import { daimiAutomotiveChecks } from '@/content/daimiAutomotive'
import { daimiBankingChecks } from '@/content/daimiBanking'
import { daimiConstructionChecks } from '@/content/daimiConstruction'
import { daimiConsumerGoodsChecks } from '@/content/daimiConsumerGoods'
import { daimiEnergyChecks } from '@/content/daimiEnergy'
import { daimiInsuranceChecks } from '@/content/daimiInsurance'
import { daimiLogisticsChecks } from '@/content/daimiLogistics'
import { daimiManufacturingChecks } from '@/content/daimiManufacturing'
import { daimiPharmaceuticalsChecks } from '@/content/daimiPharmaceuticals'
import { daimiPublicServicesChecks } from '@/content/daimiPublicServices'
import { daimiRetailChecks } from '@/content/daimiRetail'
import { daimiServicesChecks } from '@/content/daimiServices'
import { daimiTechnologyChecks } from '@/content/daimiTechnology'
import { daimiTourismChecks } from '@/content/daimiTourism'
import { daimiTurkiyeChecks } from '@/content/daimiTurkiye'
import { daimiUtilitiesChecks } from '@/content/daimiUtilities'
import { outlookAutomotiveChecks } from '@/content/outlookAutomotive'
import { outlookBankingChecks } from '@/content/outlookBanking'
import { outlookConsumerGoodsChecks } from '@/content/outlookConsumerGoods'
import { outlookEnergyChecks } from '@/content/outlookEnergy'
import { outlookInsuranceChecks } from '@/content/outlookInsurance'
import { outlookLogisticsChecks } from '@/content/outlookLogistics'
import { outlookManufacturingChecks } from '@/content/outlookManufacturing'
import { outlookPharmaceuticalChecks } from '@/content/outlookPharmaceutical'
import { outlookRetailChecks } from '@/content/outlookRetail'
import { outlookServicesChecks } from '@/content/outlookServices'
import { daimiUsaChecks } from '@/content/daimiUsa'
import { topTrends2026Checks } from '@/content/topTrends2026'
import { hasReportClosing, reportClosingCheck } from '@/content/reportClosing'
/**
 * `Report` and the helpers that build one now live in `reportBuilder.ts` —
 * a type-only re-export, so this line adds no runtime edge and the many
 * modules importing `Report` from here keep working.
 */
import type { Report } from '@/content/reportBuilder'
import { published } from '@/content/drafts'

// Imported AND re-exported, not `export type { Report } from '...'`:
// that form re-exports without binding the name locally, so `Report` in
// this file's own signatures below would resolve to whatever else is in
// scope — which it did, silently, until the structural mismatch surfaced
// three files away.
export type { Report }

/**
 * One file per report, for everything added from 2026-08-28 on.
 *
 * WHY. Appending to a single central array is what made the AI editor's
 * parallel runs collide: every report redesign added an entry here, so two
 * runs building two unrelated reports wrote the same lines and neither could
 * rebase past the other. About half of all parallel runs conflicted, and each
 * loss threw away ten minutes of agent work. A report in its own file cannot
 * collide with another report in its own file.
 *
 * The 40 entries above were deliberately NOT split when this landed. Parsing a
 * 738-line hand-written array into files is the riskiest way to get this
 * property and it buys nothing for them: they are finished, and finished
 * entries never conflict. New work is where the collisions were.
 *
 * A file exports `report`, and optionally `order` to place itself in the grid.
 * Without `order` it lands after the curated block, which is where a newly
 * appended entry would have gone anyway.
 */
const ADDED = import.meta.glob<{ report: Report; order?: number }>('./reports/*.ts', {
  eager: true,
})

/**
 * Every report, from its own file. `order` then `slug`, so the result is
 * deterministic even when two files claim the same position — which two runs
 * adding two reports at once can legitimately do, since neither can see the
 * other's number.
 *
 * **A file with no `order` sorts LAST.** The default used to be 0, which was
 * only safe while `CURATED` led this array by construction; now that the
 * thirty-one are in the glob with an explicit 0–30, a 0 default would drop
 * every new report into the middle of the index grid's hand-placed order.
 */
/**
 * Every entry, drafts included. `REPORTS` below is what ships.
 *
 * The split is deliberate and mirrors `LOCALES` / `ALL_LOCALES`: the plain
 * name is the published list, so a consumer that has never heard of drafts is
 * correct by default, and the only readers of this one are the build's draft
 * report and anything that genuinely needs the roster.
 */
export const ALL_REPORTS: Report[] =
Object.values(ADDED)
  .filter((m) => m.report)
  .sort(
    (a, b) =>
      (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
      || a.report.slug.localeCompare(b.report.slug),
  )
  .map((m) => m.report)

export const REPORTS: Report[] = published(ALL_REPORTS)

/** The reports index page's slug, in the one locale it exists in. */
export const REPORTS_INDEX = 'impact-labs/reports'

/**
 * The pages whose body is the report grid rather than their own blocks.
 *
 * Same provenance and the same shape as `PAGE_OFFICES` — attached in
 * `src/legacy.ts` at load time, not written into the imported JSON, because that
 * JSON is the record of what digitopia.co said and the grid is what we are saying
 * instead. And it *replaces* the blocks for exactly the reason the offices do: on
 * this page the block **is** the list of reports, so rendering both would print
 * every title twice.
 */
export const PAGE_REPORTS: Record<string, Report[]> = {
  [`en/${REPORTS_INDEX}`]: REPORTS,
}

/** Where a report's page lives, for a reader of a given locale. */
export function reportHref(locale: string, slug: string): string {
  return `/${locale}/${REPORTS_INDEX}/${slug}`
}

/**
 * The report gaps, in the shape `build-manifest.json` records copy checks in, so
 * `pnpm verify` refuses a production build while any report is still missing its
 * download link, its cover or its own words. Without this they would be visible
 * in a preview and silently stripped from production, which is the exact failure
 * the check channel exists to prevent.
 */
export function reportChecks(): CheckItem[] {
  return [
    ...REPORTS.filter((r) => r.check).map(
      (r): CheckItem => ({
        locale: 'en',
        page: `${REPORTS_INDEX}/${r.slug}`,
        section: 'report',
        text: r.check as string,
        // A missing download link or summary is a hole to fill, not a claim
        // somebody has to stand behind — see `CheckItem` in prerender.tsx.
        severity: 'asset',
      }),
    ),
    // The DAIMI landing page's own notes, which are the other kind: not a
    // missing file but copy transcribed off a screenshot that somebody has to
    // read against the original. Appended here rather than wired separately into
    // `prerender.tsx` because that page IS a report page, and `reportChecks()`
    // is already what the manifest asks for the reports' gaps.
    ...daimiChecks(),
    // And the Consumer Goods focus page's, for the same reason — it is the
    // second report drawn by a designed page rather than by the template.
    ...daimiConsumerGoodsChecks(),
    // And the USA Edition's, the third. Its notes are the transcription kind
    // rather than the missing-file kind: the page was built from one full-page
    // screenshot, so its figures need reading against the original.
    ...daimiUsaChecks(),
    // And the Airports focus page's, the fourth — same kind again: a page built
    // from one full-page screenshot, plus the chart and the mockup it asks for.
    ...daimiAirportsChecks(),
    // And the Türkiye edition's, the fifth. Its notes lean harder on this channel
    // than any of the others: the captures it was built from are thumbnails, so
    // the band shapes are certain and almost none of the prose is transcribed.
    ...daimiTurkiyeChecks(),
    // And the Airlines focus page's, the sixth. The only one of the six with no
    // asset note at all — it arrived with both of its figures — so what is left
    // is transcription to read back and two decisions for a person.
    ...daimiAirlinesChecks(),
    // And the Automotive focus page's, the seventh. Same kind as the Airlines
    // notes — no asset gap, so what is left is transcription to read back against
    // digitopia.co and two decisions for a person.
    ...daimiAutomotiveChecks(),
    // And the Banking focus page's, the eighth. Same kind again — no asset gap,
    // so what is left is transcription to read back against digitopia.co, the
    // assessment count the global band states, and the gating decision.
    ...daimiBankingChecks(),
    // The shared closing bands, which every report page but three now carries —
    // see `content/reportClosing.ts`. One note per page rather than one for the
    // set, because the sentence it doubts is printed on each of those pages and
    // `/__checks` is read page by page; the note itself says the answer settles
    // all of them at once.
    // `flatMap` rather than `map`, since 2026-09-01: `reportClosingCheck` returns
    // undefined for a cut of the 2026 study now that the assessment count is
    // settled, and a mapped undefined would be 17 amber notes with no text in
    // them.
    ...REPORTS.filter(hasReportClosing).flatMap((r): CheckItem[] => {
      const text = reportClosingCheck(r)
      if (!text) return []
      return [{
        locale: 'en',
        page: `${REPORTS_INDEX}/${r.slug}`,
        section: 'closing',
        text,
        severity: 'content',
      }]
    }),
    // And the Construction focus page's, the ninth. Same kind again — no asset
    // gap, so what is left is transcription to read back against digitopia.co,
    // the assessment count the global band states, and two decisions for a
    // person: whether the hero cover and the analysis mockup belong on a page
    // whose live version carries neither.
    ...daimiConstructionChecks(),
    // And the Future 2030 Automotive outlook's, the tenth — and the first that is
    // not a maturity cut. No asset gap either: it arrived with all six of its
    // images. What is left is transcription to read back against digitopia.co, one
    // link this site has no page for, one band adapted to the maturity report this
    // site actually publishes, and the gating decision.
    ...outlookAutomotiveChecks(),
    // And the Future 2030 Manufacturing outlook's, the eleventh. Same kind again,
    // and one note shorter in substance: it arrived with all six of its images, and
    // the band the Automotive page left open — which maturity report to point at —
    // was decided in this request, 2026 rather than 2024.
    ...outlookManufacturingChecks(),
    // And the Future 2030 Consumer Goods outlook's, the twelfth. The one designed
    // page whose notes are `'asset'` rather than `'content'`: four of its six
    // images are still missing, because the files that came with the request
    // belong to the Energy & Utilities outlook.
    ...outlookConsumerGoodsChecks(),
    // And the Future 2030 Retail outlook's, the thirteenth. Back to `'content'`:
    // it arrived with all six of its images, so what is left is transcription to
    // read back against digitopia.co, the Digital Upskilling substitution all four
    // outlook pages now make, one image this page shows that the reference does
    // not, and the gating decision.
    ...outlookRetailChecks(),
    // And the Future 2030 Pharmaceutical outlook's, the fourteenth. `'content'`
    // again: the five slots its bands draw all arrived. What is left is
    // transcription to read back against digitopia.co, the Digital Upskilling
    // substitution all five outlook pages now make, three places where the live
    // page still says "Retail" on a pharmaceutical page and this one does not, the
    // maturity cover the reference has no image for, and the gating decision.
    ...outlookPharmaceuticalChecks(),
    // And the Future 2030 Banking outlook's, the fifteenth. `'content'` again: it
    // arrived with all six of its images. What is left is transcription to read back
    // against digitopia.co, one average-band figure that does not match Figure 1's
    // own 2029 label, the Digital Upskilling substitution all six outlook pages now
    // make, the maturity CTA the live page still labels "Retail", two images this
    // page shows that the reference does not, and the gating decision.
    ...outlookBankingChecks(),
    // And the Future 2030 Services outlook's, the sixteenth. `'content'` again: it
    // arrived with all six of its images. What is left is transcription to read back
    // against digitopia.co — including where the hero's bold begins, which is this
    // page's own question — the Digital Upskilling substitution all seven outlook
    // pages now make, the maturity cover the reference has no image for, and the
    // gating decision.
    ...outlookServicesChecks(),
    // And the Future 2030 Insurance outlook's, the seventeenth. `'content'` again: it
    // arrived with all six of its images. What is left is transcription to read back
    // against digitopia.co — the most figure-heavy of the eight forecast bands, and
    // every number in it already reconciled against Figure 1 — the Digital Upskilling
    // substitution all eight outlook pages now make, the maturity cover the reference
    // has no image for, and the gating decision.
    ...outlookInsuranceChecks(),
    // And the Future 2030 Logistics outlook's, the eighteenth. `'content'` again: it
    // arrived with all six of its images. What is left is transcription to read back
    // against digitopia.co — including the three forecast trajectories, which were
    // re-cast so each could stand as a line of its own — the Digital Upskilling
    // substitution all nine outlook pages now make, the maturity cover the reference
    // has no image for, and the gating decision.
    ...outlookLogisticsChecks(),
    // And the Future 2030 Energy & Utilities outlook's, the nineteenth. `'content'`
    // again: it arrived with all six of its images. What is left is transcription to
    // read back against digitopia.co — the three forecast trajectories were re-cast so
    // each could stand as a line of its own, though every figure in them reconciles
    // against Figure 1 — which of the three imperative photographs belongs on which
    // card, since the reference renders that band with all three missing, the Digital
    // Upskilling substitution all ten outlook pages now make, whether the maturity band
    // should point at the Energy cut or the Utilities one, the "Insurance" the live
    // closing band still says on this page, the maturity cover the reference has no
    // image for, and the gating decision.
    ...outlookEnergyChecks(),
    // And Top 10 Trends 2026's, the twentieth — the first designed page that is neither
    // a maturity cut nor a Future 2030 outlook. `'content'` again: it arrived with all
    // three of its mockups. What is left is transcription to read back against
    // digitopia.co, four audience blurbs written here because the reference capture
    // shows those accordion panels closed, which of the three mockups belongs beside
    // which band since the capture renders all three slots empty, the maturity band that
    // is an addition rather than a transcription, and the gating decision.
    ...topTrends2026Checks(),
    // And the Utilities focus page's, the twenty-first — back to a maturity cut.
    // `'content'` again: it arrived with its cover, Figure 1 and the mockup. What is
    // left is transcription to read back against digitopia.co, the two slots the
    // live page leaves empty that this one fills (the hero cover and the analysis
    // mockup), and the gating decision. The one thing it does *not* raise is the
    // closing band's destination: that was decided in this request — 2026, not 2024
    // — and it is already where `content/reportClosing.ts` points.
    ...daimiUtilitiesChecks(),
    // And the Tourism focus page's, the twenty-second — the Utilities page's shape
    // with a different sector in it, so the same four notes. `'content'` again: it
    // arrived with its cover, Figure 1 and the mockup. What is left is transcription
    // to read back against digitopia.co, the two slots the live page leaves empty
    // that this one fills (the hero cover and the analysis mockup), and the gating
    // decision. The closing band's destination is not among them: that was decided
    // in this request too — 2026, not 2024 — and it is already where
    // `content/reportClosing.ts` points.
    ...daimiTourismChecks(),
    // And the Technology focus page's, the twenty-third — the Tourism page's shape
    // with a different sector in it, so the same four notes again. `'content'`: it
    // arrived with its cover, Figure 1 and the mockup. What is left is transcription
    // to read back against digitopia.co, the two slots the live page leaves empty
    // that this one fills (the hero cover and the analysis mockup), and the gating
    // decision. The closing band's destination is not among them: that was decided
    // in this request too — 2026, not 2024 — and it is already where
    // `content/reportClosing.ts` points.
    ...daimiTechnologyChecks(),
    // And the Services focus page's, the twenty-fourth — the Technology page's shape
    // with a different sector in it, so the same four notes again, and Figure 1 is the
    // six-year study-results chart the Banking cut carries rather than a vitality
    // scatter. `'content'`: it arrived with its cover, Figure 1 and the mockup. What
    // is left is transcription to read back against digitopia.co, the two slots the
    // live page leaves empty that this one fills (the hero cover and the analysis
    // mockup), and the gating decision. The closing band's destination is not among
    // them: that was decided in this request too — 2026, not 2024 — and it is already
    // where `content/reportClosing.ts` points.
    ...daimiServicesChecks(),
    // And the Retail focus page's, the twenty-fifth — the Services page's shape with
    // a different sector in it, so the same four notes again, and Figure 1 is the
    // six-year study-results chart again rather than a vitality scatter. `'content'`:
    // it arrived with its cover, Figure 1 and the mockup. What is left is
    // transcription to read back against digitopia.co, the two slots the live page
    // leaves empty that this one fills (the hero cover and the analysis mockup), and
    // the gating decision. The closing band's destination is not among them: that was
    // decided in this request too — 2026, not 2024 — and it is already where
    // `content/reportClosing.ts` points.
    ...daimiRetailChecks(),
    // And the Public Services focus page's, the twenty-sixth — the Retail page's shape
    // with a different sector in it and a vitality scatter back in Figure 1's slot, so
    // the same four notes again. `'content'`: it arrived with its cover, Figure 1 and
    // the mockup. What is left is transcription to read back against digitopia.co, the
    // two slots the live page leaves empty that this one fills (the hero cover and the
    // analysis mockup), and the gating decision. The closing band's destination is not
    // among them: that was decided in this request too — 2026, not 2024 — and it is
    // already where `content/reportClosing.ts` points.
    ...daimiPublicServicesChecks(),
    // And the Pharmaceuticals focus page's, the twenty-seventh — the Retail page's
    // shape with a different sector in it and the six-year study-results chart back in
    // Figure 1's slot, so the same four notes again. `'content'`: it arrived with its
    // cover, Figure 1 and the mockup. What is left is transcription to read back
    // against digitopia.co — including the 2.90 cross-industry baseline the hero
    // states, which is the one figure on the page that none of the supplied files can
    // confirm — the two slots the live page leaves empty that this one fills (the hero
    // cover and the analysis mockup), and the gating decision. The closing band's
    // destination is not among them: that was decided in this request too — 2026, not
    // 2024 — and it is already where `content/reportClosing.ts` points.
    ...daimiPharmaceuticalsChecks(),
    // And the Manufacturing focus page's, the twenty-eighth — the Pharmaceuticals
    // page's shape with a different sector in it and the six-year study-results chart
    // in Figure 1's slot again, so four notes again, though one of them is shorter
    // than its neighbours': this is the first focus cut whose live page already
    // carries both the hero cover and the analysis mockup, so neither is an addition
    // to argue for. `'content'`: it arrived with its cover, Figure 1 and the mockup.
    // What is left is transcription to read back against digitopia.co — including the
    // cross-industry baseline the hero says the sector aligns with "precisely", which
    // none of the supplied files print — and the gating decision. The closing band's
    // destination is not among them: that was decided in this request too — 2026, not
    // 2024 — and it is already where `content/reportClosing.ts` points.
    ...daimiManufacturingChecks(),
    // And the Logistics focus page's, the twenty-ninth — the Manufacturing page's
    // shape with a different sector in it and the six-year study-results chart in
    // Figure 1's slot again, so four notes again. `'content'`: it arrived with its
    // cover, Figure 1 and the mockup. What is left is transcription to read back
    // against digitopia.co — including the 2.93 cross-industry baseline the hero
    // states, which is the one figure on the page none of the supplied files can
    // confirm — the two slots the live page leaves empty that this one fills (the
    // hero cover and the analysis mockup), and the gating decision. The closing
    // band's destination is not among them: that was decided in this request too —
    // 2026, not 2024 — and it is already where `content/reportClosing.ts` points.
    ...daimiLogisticsChecks(),
    // And the Insurance focus page's, the thirtieth — the Logistics page's shape with a
    // different sector in it, so four notes again. `'content'`: it arrived with its
    // cover, Figure 1 and the mockup. What is left is transcription to read back
    // against digitopia.co, one reading of Figure 1 the prose does not make (the
    // average has been flat at 2.99 for two years while the leader kept climbing, and
    // the laggard is still below its 2023 value), the two slots the live page leaves
    // empty that this one fills (the hero cover and the analysis mockup), and the
    // gating decision. The closing band's destination is not among them: that was
    // decided in this request too — 2026, not 2024 — and it is already where
    // `content/reportClosing.ts` points.
    ...daimiInsuranceChecks(),
    // And the Energy focus page's, the thirty-first — the Insurance page's shape with a
    // different sector in it and the six-year study-results chart in Figure 1's slot
    // again. Three notes rather than four this time: the live analysis band already
    // carries the mockup, so that column is a transcription and not an addition to argue
    // for. `'content'`: it arrived with its cover, Figure 1 and the mockup. What is left
    // is transcription to read back against digitopia.co — including the 2.93
    // cross-industry baseline the hero states, which is the one figure on the page none
    // of the supplied files can confirm, though it is the same baseline the Logistics cut
    // prints — two readings of Figure 1 the prose does not make (the leader-to-laggard
    // spread is widening rather than the middle rising, and the laggard went backwards to
    // 2.16 in 2025), the hero cover the live page leaves out, and the gating decision.
    // The closing band's destination is not among them: that was decided in this request
    // too — 2026, not 2024 — and it is already where `content/reportClosing.ts` points.
    ...daimiEnergyChecks(),
  ]
}
